import { Header, Spinner, BackButton } from 'view/components';
import { PublicHeader } from 'view/components/PublicHeader';
import { useNavigate, useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import { useGetJobPostInfoById } from 'view/pages/candidates/hooks/useGetJobPostInfoById';
import { useGetPublicJobPostById } from 'view/hooks/useGetPublicJobPostById';
import { ViewJobPostBlock } from 'view/pages/createJobPost/components';
import { ChatSocketProvider } from 'App';
import { storageService } from 'infrastructure/services/StorageService';
import { useGoBack } from 'view/hooks/useGoBack';
import * as S from './styled';

const ViewJobPostPage = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  // '/' is SmartRedirect: it picks the list this user came from by role (job seeker → job list,
  // recruiter → my job posts, guest → /jobs).
  const goBack = useGoBack('/');
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [token, setToken] = useState<string | undefined>(undefined);

  // Support both route params (/jobs/:id) and query params (/profile/view-job-post?id=5)
  const params = new URLSearchParams(window.location.search);
  const queryId = params.get('id');
  const id = routeId || queryId;

  // Use different hooks based on authentication
  const { isLoading: isLoadingAuth, getJobPostInfoById, data: dataAuth } = useGetJobPostInfoById();
  const {
    isLoading: isLoadingPublic,
    getPublicJobPostById,
    data: dataPublic,
  } = useGetPublicJobPostById();

  // Public route /jobs/:id reads the public API, /profile/view-job-post?id=X the authenticated
  // one. The effect below sends each visitor to the route that matches their auth state, so by
  // the time anything renders these two conditions agree.
  const isPublicRoute = !!routeId;
  const usePublicApi = isPublicRoute || !isAuthenticated;

  const isLoading = usePublicApi ? isLoadingPublic : isLoadingAuth;
  const data = usePublicApi ? dataPublic : dataAuth;

  // Fetch token for ChatSocketProvider
  useEffect(() => {
    const fetchToken = async () => {
      if (isAuthenticated) {
        const t = await storageService.getItem('accessToken');
        setToken(t);
      }
    };
    fetchToken();
  }, [isAuthenticated]);

  useEffect(() => {
    // `isAuthenticated` is false until the tokens are read out of storage, so running any of the
    // branches below before that resolves would treat an authenticated user as a guest.
    if (isAuthLoading) return;

    if (!id) {
      navigate(isAuthenticated ? '/profile/job-list' : '/jobs', { replace: true });
      return;
    }

    // Keep the route in sync with the auth state — the two views are not interchangeable.
    // /jobs/:id renders public data (no application status, no match score, no refetch after
    // applying) and its CTA is "Sign In to Apply"; /profile/view-job-post?id=X is the full one.
    // Without this, an authenticated user opening a shared /jobs/:id link was offered a sign-in
    // that AuthRedirect immediately bounced to /profile/profile-settings.
    if (isAuthenticated && isPublicRoute) {
      navigate(`/profile/view-job-post?id=${id}`, { replace: true });
      return;
    }

    if (!isAuthenticated && !isPublicRoute) {
      navigate(`/jobs/${id}`, { replace: true });
      return;
    }

    if (usePublicApi) {
      getPublicJobPostById(id);
    } else {
      getJobPostInfoById(id);
    }
  }, [id, isAuthenticated, isAuthLoading, isPublicRoute]);

  // One of the two redirects above is pending: the route and the auth state disagree, so this
  // render would flash the wrong view and fetch from an endpoint the other route re-fetches.
  const isRedirecting = !!id && !isAuthLoading && isAuthenticated === isPublicRoute;

  // Show loading while auth or token is loading, or while a redirect is in flight
  if (isAuthLoading || isRedirecting || (isAuthenticated && !token)) {
    return (
      <S.Wrapper>
        <Spinner variant="outlined" style={{ position: 'absolute', top: '50%', left: '50%' }} />
      </S.Wrapper>
    );
  }

  const pageContent = (
    <S.Wrapper>
      {isAuthenticated ? <Header /> : <PublicHeader />}
      {/* <S.BreadcrumbsContainer /> */}
      <S.BackButtonBox>
        <BackButton text="Back" onClick={goBack} />
      </S.BackButtonBox>
      {isLoading || !data ? (
        <S.MainContentWrapper
          style={{
            background: '#fff',
            height: 'calc(100vh - 120px)',
            borderRadius: '24px',
            padding: '24px',
          }}
        >
          <Spinner variant="outlined" style={{ position: 'absolute', top: '50%', left: '50%' }} />
        </S.MainContentWrapper>
      ) : (
        <S.MainContentWrapper>
          <ViewJobPostBlock
            data={data}
            // Gates the apply flow, so it follows the auth state — not the endpoint the data came
            // from. The redirect above keeps the two equal, but the flag has to mean what it says.
            isPublic={!isAuthenticated}
            getJobPostInfoById={
              !usePublicApi
                ? () => {
                    return getJobPostInfoById(id);
                  }
                : undefined
            }
          />
        </S.MainContentWrapper>
      )}
    </S.Wrapper>
  );

  // Wrap with ChatSocketProvider for authenticated users
  if (isAuthenticated && token) {
    return <ChatSocketProvider token={token}>{pageContent}</ChatSocketProvider>;
  }

  return pageContent;
};

export default ViewJobPostPage;
